import type { ApiErrorCode, VerifyEmailRequest } from "@9amhealth/openapi";
import { UserEmailVerificationControllerService } from "@9amhealth/openapi";
import { Cubit } from "blac";
import { Blac } from "blac-next";
import { addSentryBreadcrumb } from "src/lib/addSentryBreadcrumb";
import { extractErrorCode, extractErrorCodeString } from "src/lib/errors";
import reportErrorSentry from "src/lib/reportErrorSentry";
import LoadingCubit, { LoadingKey } from "src/state/LoadingCubit/LoadingCubit";
import type { UserEmailVerificationStateInitial } from "src/state/UserEmailVerificationCubit/UserEmailVerificationState";
import { UserEmailVerificationStateName } from "src/state/UserEmailVerificationCubit/UserEmailVerificationState";
import { apiMiddleware, userState } from "src/state/state";
import { onUserSessionReset } from "src/lib/onUserSessionReset";

const RESEND_COOLDOWN_SECONDS = 300; // 5 minutes
const RESEND_TIMER_STORAGE_KEY = "emailVerification_resendCooldownStart";
const VERIFICATION_TOKEN_STORAGE_KEY = "emailVerification_token";

export default class UserEmailVerificationCubit extends Cubit<UserEmailVerificationStateInitial> {
  constructor() {
    const persistedToken =
      localStorage.getItem(VERIFICATION_TOKEN_STORAGE_KEY) ?? undefined;

    super({
      stateName: UserEmailVerificationStateName.unverified,
      verificationToken: persistedToken
    });

    // The token and the cooldown belong to one member's email; both live outside
    // the per-user storage namespace, so they have to be dropped explicitly when
    // the session's user changes.
    onUserSessionReset(() => {
      localStorage.removeItem(VERIFICATION_TOKEN_STORAGE_KEY);
      localStorage.removeItem(RESEND_TIMER_STORAGE_KEY);
      this.emit({
        stateName: UserEmailVerificationStateName.unverified,
        verificationToken: undefined
      });
    });
  }

  /**
   * Get the resend cooldown duration in seconds
   */
  public get resendCooldownSeconds(): number {
    return RESEND_COOLDOWN_SECONDS;
  }

  /**
   * Calculate remaining seconds from stored timestamp
   */
  public getRemainingSeconds(): number {
    const storedTimestamp = localStorage.getItem(RESEND_TIMER_STORAGE_KEY);
    if (!storedTimestamp) {
      return 0;
    }

    const startTime = parseInt(storedTimestamp, 10);
    const elapsed = Math.floor((Date.now() - startTime) / 1000);
    const remaining = RESEND_COOLDOWN_SECONDS - elapsed;

    return remaining > 0 ? remaining : 0;
  }

  /**
   * Check if resend is currently allowed
   */
  public canResend(): boolean {
    return this.getRemainingSeconds() === 0;
  }

  /**
   * Store the current timestamp as cooldown start
   */
  public storeCooldownStart(): void {
    localStorage.setItem(RESEND_TIMER_STORAGE_KEY, Date.now().toString());
  }

  /**
   * Clear the cooldown timer
   */
  public clearCooldown(): void {
    localStorage.removeItem(RESEND_TIMER_STORAGE_KEY);
  }

  /**
   * Store the verification token (e.g., from registration response)
   */
  public readonly setVerificationToken = (token: string): void => {
    localStorage.setItem(VERIFICATION_TOKEN_STORAGE_KEY, token);
    this.emit({
      ...this.state,
      verificationToken: token
    });
  };

  /**
   * Get the current verification token
   */
  public get verificationToken(): string | undefined {
    return this.state.verificationToken;
  }

  /**
   * Clear the verification token
   */
  public readonly clearVerificationToken = (): void => {
    localStorage.removeItem(VERIFICATION_TOKEN_STORAGE_KEY);
    this.emit({
      ...this.state,
      verificationToken: undefined
    });
  };

  /**
   * Clean up all persisted verification data (token + cooldown)
   */
  private cleanupPersistedData(): void {
    localStorage.removeItem(VERIFICATION_TOKEN_STORAGE_KEY);
    this.clearCooldown();
  }

  /**
   * Resend verification email and get a new token
   */
  public readonly resendVerification = async (
    email: string
  ): Promise<string | undefined> => {
    const token = await this.initiateVerification(email);
    this.storeCooldownStart();

    return token;
  };

  public readonly initiateVerification = async (
    email?: string
  ): Promise<string | undefined> => {
    apiMiddleware.clearAll();
    const loadingState = Blac.getBloc(LoadingCubit);
    loadingState.start(LoadingKey.initiateVerification);
    let error;
    let token: string | undefined;

    addSentryBreadcrumb("auth", `Initiating email verification`);

    if (email) {
      const data = { email };

      try {
        const response =
          await UserEmailVerificationControllerService.initiateEmailVerification(
            data
          );
        token = response.data.verificationToken;
        if (token) {
          this.setVerificationToken(token);
        }
      } catch (e: unknown) {
        error = e as Error;
        reportErrorSentry(e);
      }
    } else {
      reportErrorSentry(new Error("No email provided"));
    }

    loadingState.finish(LoadingKey.initiateVerification);

    if (error) {
      throw error;
    }

    return token;
  };

  public resendFailed = (): void => {
    this.emit({
      ...this.state,
      stateName: UserEmailVerificationStateName.resendFailed
    });
  };

  /**
   * Verify email using the 6-digit code
   */
  public readonly verifyByCode = async (data: {
    email: string;
    code: string;
    verificationToken: string;
  }): Promise<{ success: boolean; errorCode?: ApiErrorCode }> => {
    const loadingState = Blac.getBloc(LoadingCubit);
    loadingState.start(LoadingKey.verifyEmail);

    try {
      await UserEmailVerificationControllerService.completeEmailVerification({
        email: data.email,
        verificationCode: data.code,
        verificationToken: data.verificationToken
      });

      await userState.setUserInfo();

      this.cleanupPersistedData();
      this.emit({
        ...this.state,
        verificationToken: undefined,
        stateName: UserEmailVerificationStateName.verified
      });

      addSentryBreadcrumb("auth", `Successfully verified email by code`);
      loadingState.finish(LoadingKey.verifyEmail);

      return { success: true };
    } catch (e: unknown) {
      this.emit({
        ...this.state,
        stateName: UserEmailVerificationStateName.verificationFailed
      });

      addSentryBreadcrumb("auth", `Failed to verify email by code`);
      loadingState.finish(LoadingKey.verifyEmail);

      const errorCode = extractErrorCode(e);
      reportErrorSentry(e);

      return { success: false, errorCode };
    }
  };

  public readonly verifyEmailByLink = async (data: {
    linkVerificationToken: string;
  }): Promise<void> => {
    const loadingState = Blac.getBloc(LoadingCubit);
    loadingState.start(LoadingKey.verifyEmail);
    let error;

    try {
      const requestBody = {
        linkVerificationToken: data.linkVerificationToken
      };
      await UserEmailVerificationControllerService.completeEmailVerification(
        requestBody
      );

      void userState.setUserInfo();

      this.cleanupPersistedData();
      this.emit({
        ...this.state,
        verificationToken: undefined,
        stateName: UserEmailVerificationStateName.verified
      });

      addSentryBreadcrumb("auth", `Successfully verified email`);
    } catch (e: unknown) {
      error = e as Error;
      this.emit({
        ...this.state,
        stateName: UserEmailVerificationStateName.verificationFailed
      });

      addSentryBreadcrumb("auth", `Failed to verify email`);
    }
    loadingState.finish(LoadingKey.verifyEmail);

    if (error) {
      throw error;
    }
  };

  public readonly verifyEmailStateless = async (
    data: VerifyEmailRequest
  ): Promise<{ error: string }> => {
    const loadingState = Blac.getBloc(LoadingCubit);
    loadingState.start(LoadingKey.verifyEmail);
    let error = "";

    try {
      await UserEmailVerificationControllerService.completeEmailVerification(
        data
      );

      addSentryBreadcrumb("auth", `Successfully verified email stateless`);
    } catch (e: unknown) {
      reportErrorSentry(e);
      error = extractErrorCodeString(e);
    }
    loadingState.finish(LoadingKey.verifyEmail);
    return { error };
  };
}
